import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { Tooltip } from 'antd';
import { Icon } from 'Components/UI';
import AvatarsItems from 'Pages/Activities/components/AvatarsItems';
import { getListActivities } from 'Store/Activities/Activities.selector';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { getDisciplineIcon } from 'Utils/ObraUtils';
import { handlePriority, handleVisibility } from 'Utils/ActivitiesUtils';
import { TypeIconsEnum } from 'Data/interfaces/Activities/IDragAndDropDataFormat';
import styles from './AiActivityCard.module.scss';

interface AiActivityCardProps {
  number?: string;
  title: string;
  link?: string;
  priority?: string;
  deadline?: string;
  visibility?: string;
  responsibles?: string;
  disciplines?: string;
}

const checkIsOverdue = (deadline: string): boolean => {
  const isoMatch = deadline.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const d = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}T23:59:59`);
    return d < new Date();
  }
  const brMatch = deadline.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (brMatch) {
    const d = new Date(parseInt(brMatch[3]), parseInt(brMatch[2]) - 1, parseInt(brMatch[1]), 23, 59, 59);
    return d < new Date();
  }
  return deadline.toLowerCase().includes('atrasad');
};

const formatDeadlineShort = (deadline: string): string => {
  const isoMatch = deadline.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}`;
  const brFull = deadline.match(/^(\d{2}\/\d{2})\/\d{4}/);
  if (brFull) return brFull[1];
  return deadline;
};

const formatDeadline = (deadline: string): string => {
  const overdue = checkIsOverdue(deadline);
  const suffix = overdue ? ' (atrasada)' : '';
  const isoMatch = deadline.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}${suffix}`;
  }
  const brFull = deadline.match(/^(\d{2}\/\d{2})\/\d{4}/);
  if (brFull) return `${brFull[1]}${suffix}`;
  return deadline;
};

export const AiActivityCard: FC<AiActivityCardProps> = ({
  number,
  title,
  link,
  priority,
  deadline,
  visibility,
  responsibles,
  disciplines,
}) => {
  const { t } = useTranslation('activities');
  const navigate = useNavigate();
  const location = useLocation();
  const isOverdue = deadline ? checkIsOverdue(deadline) : false;
  const dotColor = '#BEC2C6';
  const listActivities = useSelector(getListActivities);
  const theme = useSelector(getTheme);

  const fullActivity = useMemo(() => {
    if (!number || !listActivities?.activities) return null;
    return Object.values(listActivities.activities).find(
      (a) => String(a.Identifier) === number
    ) || null;
  }, [number, listActivities]);

  const statusColor = useMemo(() => {
    if (fullActivity?.Status?.Color) return fullActivity.Status.Color;
    const name = fullActivity?.Status?.Name?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') ?? '';
    if (name.includes('aguardando')) return '#BEC2C6';
    if (name.includes('pendente')) return '#F5A623';
    if (name.includes('resolvido')) return '#52C41A';
    if (name.includes('cancelado')) return '#E2574C';
    return null;
  }, [fullActivity]);

  const priorityInfo = useMemo(() => {
    if (fullActivity?.Priority != null) {
      return handlePriority(fullActivity.Priority, theme);
    }
    if (!priority) return null;
    const lower = priority.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (lower.includes('alta')) return { icon: 'flag' as const, color: theme.colors.danger.danger, size: 14, label: t('aiChat.priorityHigh') };
    if (lower.includes('media') || lower.includes('média')) return { icon: 'flag' as const, color: theme.colors.warning.warning, size: 14, label: t('aiChat.priorityMedium') };
    if (lower.includes('baixa')) return { icon: 'flag' as const, color: theme.colors.surface.onSurfacePlaceholder, size: 14, label: t('aiChat.priorityLow') };
    return null;
  }, [fullActivity, priority, theme, t]);

  const visibilityInfo = useMemo(() => {
    if (fullActivity?.Visibility != null) {
      return handleVisibility(fullActivity.Visibility, theme);
    }
    if (!visibility) return null;
    const lower = visibility.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (lower.includes('publica') || lower.includes('público') || lower.includes('publico')) {
      return { icon: 'globe' as const, color: theme.colors.information.information, size: 16, label: t('aiChat.visibilityPublic') };
    }
    if (lower.includes('privado') || lower.includes('restrito')) {
      return { icon: 'cadeadoFechado' as const, color: theme.colors.surface.onSurfaceVariant, size: 16, label: t('aiChat.visibilityPrivate') };
    }
    return { icon: 'editar' as const, color: theme.colors.surface.onSurfacePlaceholder, size: 12, label: t('aiChat.visibilityDraft') };
  }, [fullActivity, visibility, theme, t]);

  const handleClick = () => {
    if (fullActivity?.Guid) {
      const params = new URLSearchParams(location.search);
      params.set('workActivity', fullActivity.Guid);
      navigate(`${location.pathname}?${params.toString()}`);
      return;
    }
    if (!link) return;
    try {
      const url = new URL(link);
      const productionDomains = ['app.maletadoengenheiro.com.br', 'app.coordly.com.br'];
      const isInternal =
        productionDomains.some((d) => url.hostname.includes(d)) ||
        url.hostname === window.location.hostname;
      if (isInternal) {
        const redirectUrl = url.searchParams.get('redirectUrl');
        navigate(redirectUrl ? decodeURIComponent(redirectUrl) : url.pathname + url.search + url.hash);
      } else {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    } catch {
      if (link.startsWith('/')) navigate(link);
    }
  };

  return (
    <div className={styles.activityWrapper}>
      <span className={styles.bullet} style={{ backgroundColor: theme.colors.surface.onSurface }} />

      <div className={styles.activityContent}>
        <div className={styles.titleTooltipWrapper}>
          <div
            className={styles.titlePill}
            style={statusColor ? { borderLeft: `3px solid ${statusColor}` } : undefined}
            onClick={link || fullActivity?.Guid ? handleClick : undefined}
            role={link || fullActivity?.Guid ? 'button' : undefined}
          >
            <Icon icon={(TypeIconsEnum[fullActivity?.Type?.Name as keyof typeof TypeIconsEnum] ?? 'activities') as any} customSize={14} customColor="#8697A8" />
            <span className={styles.separator} />
            {number && <span className={styles.numberBadge}>#{number}</span>}
            <span className={styles.activityTitle}>{title}</span>
          </div>

          <div className={styles.activityTooltip}>
            <div className={styles.tooltipCard}>
              <div className={styles.tooltipHeader}>
                <div className={styles.tooltipHeaderLeft}>
                  <div className={styles.tooltipIdRow}>
                    {number && <span className={styles.tooltipNumber}>#{number}</span>}
                    <Icon icon={(TypeIconsEnum[fullActivity?.Type?.Name as keyof typeof TypeIconsEnum] ?? 'activities') as any} customSize={13} customColor="#8697A8" />
                    <span className={styles.tooltipType}>{fullActivity?.Type?.Name || t('aiChat.defaultType')}</span>
                    <span className={styles.tooltipStatusDot} />
                  </div>
                  <span className={styles.tooltipTitle}>{title}</span>
                </div>
                {fullActivity?.Attachments && fullActivity.Attachments.length > 0 ? (
                  <img
                    src={fullActivity.Attachments[0].Thumbnail128x128Url}
                    alt={fullActivity.Attachments[0].File.Name}
                    className={styles.tooltipPreviewImg}
                  />
                ) : null}
              </div>

              {deadline && (
                <Tooltip title={isOverdue ? 'Prazo vencido' : formatDeadline(deadline)} placement="top">
                  <div className={styles.tooltipDateRow}>
                    <Icon icon="calendar" customSize={13} customColor={isOverdue ? '#E2574C' : '#8697A8'} />
                    <span className={isOverdue ? styles.overdue : styles.tooltipMetaValue}>
                      {formatDeadlineShort(deadline)}
                    </span>
                  </div>
                </Tooltip>
              )}

              <div className={styles.tooltipBottomRow}>
                <div className={styles.tooltipBottomLeft}>
                  {(fullActivity?.TopicAssignedUsers?.length ?? 0) > 0 && (
                    <AvatarsItems users={fullActivity!.TopicAssignedUsers} showDetail />
                  )}
                  {!fullActivity && responsibles && (
                    <div className={styles.tooltipAvatarRow}>
                      {responsibles.split(',').slice(0, 3).map((name) => {
                        const trimmed = name.trim();
                        if (!trimmed) return null;
                        const initials = trimmed.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
                        return (
                          <span key={trimmed} className={styles.tooltipAvatar} title={trimmed}>{initials}</span>
                        );
                      })}
                      {responsibles.split(',').length > 3 && (
                        <span className={styles.tooltipCountBadge}>+{responsibles.split(',').length - 3}</span>
                      )}
                    </div>
                  )}

                  {((fullActivity?.TopicAssignedUsers?.length ?? 0) > 0 || (!fullActivity && responsibles)) &&
                   ((fullActivity?.ConstructionSiteDisciplines?.length ?? 0) > 0 || (!fullActivity && disciplines)) && (
                    <span className={styles.tooltipRowDivider} />
                  )}

                  {(fullActivity?.ConstructionSiteDisciplines?.length ?? 0) > 0 && (() => {
                    const discs = fullActivity!.ConstructionSiteDisciplines;
                    const visible = discs.slice(0, 3);
                    const extra = discs.length - 3;
                    return (
                      <div className={styles.tooltipDisciplinesRow}>
                        {visible.map((disc, idx) => {
                          const discName = disc.CustomName || disc.Discipline?.Name;
                          return (
                            <Tooltip key={`${disc.ConstructionSiteDisciplinesId}-${idx}`} title={discName} placement="top">
                              <span className={styles.tooltipDisciplineIcon}>
                                <Icon
                                  icon={getDisciplineIcon(discName)}
                                  customSize={getDisciplineIcon(discName) === 'menuContexto' ? 12 : 14}
                                  customColor={theme.colors.surface.onSurfaceVariant}
                                />
                              </span>
                            </Tooltip>
                          );
                        })}
                        {extra > 0 && <span className={styles.tooltipCountBadge}>+{extra}</span>}
                      </div>
                    );
                  })()}
                  {!fullActivity && disciplines && (
                    <div className={styles.tooltipDisciplinesRow}>
                      {disciplines.split(',').slice(0, 4).map((disc) => {
                        const trimmed = disc.trim();
                        if (!trimmed) return null;
                        return (
                          <span key={trimmed} className={styles.tooltipChip}>{trimmed}</span>
                        );
                      })}
                      {disciplines.split(',').length > 4 && (
                        <span className={styles.tooltipCountBadge}>+{disciplines.split(',').length - 4}</span>
                      )}
                    </div>
                  )}
                </div>

                {(priorityInfo || visibilityInfo) && (
                  <div className={styles.tooltipBottomRight}>
                    {priorityInfo && (
                      <Icon icon={priorityInfo.icon} customSize={priorityInfo.size || 14} customColor={priorityInfo.color} />
                    )}
                    {visibilityInfo && (
                      <Icon icon={visibilityInfo.icon} customSize={visibilityInfo.size || 16} customColor={visibilityInfo.color} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {deadline && (
          <div className={styles.metaRow}>
            <span className={styles.metaIcon}>📅</span>
            <span className={styles.metaLabel}>{t('aiChat.originalDeadline')} </span>
            <span className={isOverdue ? styles.overdue : styles.metaValue}>
              {formatDeadline(deadline)}
            </span>
          </div>
        )}

        {priority && (
          <div className={styles.metaRow}>
            <span className={styles.metaIcon}>🏷️</span>
            <span className={styles.metaLabel}>{t('aiChat.priorityLabel')} </span>
            <span className={styles.metaValue}>{priority}</span>
          </div>
        )}
      </div>
    </div>
  );
};
